import { type AriaAttributes, type KeyboardEventHandler, type Ref } from 'react'
import cx from 'classnames'
import { SearchField } from '@/components/SearchField'
import { EMPTY, type Observable } from 'rxjs'
import styles from './ModalSheetSearchHeader.module.css'
import {
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
} from '@/components/ModalSheet'

export interface ModalSheetSearchHeaderProps extends Pick<
  AriaAttributes,
  | 'aria-label'
  | 'aria-expanded'
  | 'aria-controls'
  | 'aria-activedescendant'
  | 'aria-autocomplete'
> {
  /**
   * `combobox` when the field picks from a list of results with the arrow
   * keys, like a command palette's.
   */
  role?: 'combobox'
  value: string
  onChange: (newValue: string) => unknown
  placeholder?: string
  focus$?: Observable<unknown>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  /**
   * Shows the close button while the search field is empty.
   */
  withCloseButton?: boolean
  bordered?: boolean
  /**
   * If `true`, will add border radius to the bottom as well since there is
   * no sibling to add it.
   */
  lonelyChild?: boolean
  ref?: Ref<HTMLInputElement>
  testId?: string
}

/**
 * Modal sheet header with a search bar.
 */
export function ModalSheetSearchHeader({
  value,
  onChange,
  placeholder,
  focus$ = EMPTY,
  onKeyDown,
  withCloseButton,
  bordered,
  lonelyChild,
  ref,
  testId,
  ...aria
}: ModalSheetSearchHeaderProps) {
  const canClose = withCloseButton && !value

  return (
    <ModalSheetHeaderShell
      size="sm"
      lonelyChild={lonelyChild}
      bordered={bordered}
    >
      <div className={cx(styles.root, !canClose && styles.full)}>
        <div className={styles.field}>
          <SearchField
            autoFocus
            stretch
            testId={testId}
            focus$={focus$}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            ref={ref}
            {...aria}
          />
        </div>

        <div className={cx(styles.closeBtn, canClose && styles.closeVisible)}>
          <ModalSheetHeaderCloseButton />
        </div>
      </div>
    </ModalSheetHeaderShell>
  )
}
